// @ts-check
// pwa/lib/header.js — universal navigation header (app-shell orchestrator).
//
// App-shell architecture (2026-05-29 navigation research): render the header
// ONCE on boot into the persistent #app-header container, then update only its
// content reactively when $session changes or the route changes. View mounts
// render into #app-main only — the header never re-paints on navigation.
//
// THIS FILE OWNS the contract seam (mountHeader / setActiveRoute), $session
// reactivity, and visibility. The VISUAL is now the ported spec-003 shell:
// components/app-header.js composes the bar + tab-nav + profile sheet + bell
// popover + sign-out modal. The six-point contract lives there + in
// components/tab-nav.js + components/profile-sheet.js (see docs/UNIVERSAL_HEADER.md).

import { html, render } from 'lit-html';
import { $session, $unconfirmedCount } from './store.js';
import { api } from './api.js';
import { appHeaderTemplate, wireAppHeader } from '../components/app-header.js';
import { refreshBell, stopBellPoll } from '../components/notification-popover.js';

/** @type {HTMLElement | null} */
let HEADER_EL = null;
/** Latest known pathname — used to mark the active link on $session change. */
let LATEST_PATH = '/';

/**
 * Mount the universal header into the given container. Idempotent.
 * @param {HTMLElement} root
 */
export function mountHeader(root) {
  HEADER_EL = root;
  LATEST_PATH = window.location.pathname;
  paint();
  // Reactive: re-render whenever $session flips (admin links appear when
  // is_admin; header hides on signout).
  $session.subscribe(() => paint());
  // F016: re-paint the "N to confirm" badge when the unconfirmed count changes
  // (dashboard load + each confirm). skipFirst — the initial value paints above.
  let firstCount = true;
  $unconfirmedCount.subscribe(() => { if (firstCount) { firstCount = false; return; } paint(); });
}

/**
 * Set the active route. Call after every dispatch — flips aria-current="page"
 * on the matching data-nav link(s) without re-painting the chrome.
 * @param {string} pathname
 */
export function setActiveRoute(pathname) {
  LATEST_PATH = pathname;
  if (!HEADER_EL) return;
  HEADER_EL.querySelectorAll('a[data-nav]').forEach((a) => {
    if (a.getAttribute('href') === pathname) a.setAttribute('aria-current', 'page');
    else a.removeAttribute('aria-current');
  });
}

function paint() {
  if (!HEADER_EL) return;
  const me = $session.get();
  // Auth screens render full-bleed without a header — hide the container.
  if (!me) {
    HEADER_EL.setAttribute('hidden', '');
    render(html``, HEADER_EL);
    stopBellPoll();
    return;
  }
  HEADER_EL.removeAttribute('hidden');
  // Contract item 4: drive [data-admin-only] reveal off the body attribute
  // (defence-in-depth alongside the template's own gating). isAdmin stays a
  // strict (env.ADMIN_EMAILS) flag; adminArea also opens for Coordinators
  // (Feature 010) so the Admin tab + sub-nav reveal for the tier below admin.
  document.body.dataset.isAdmin = me.is_admin ? 'true' : 'false';
  document.body.dataset.adminArea = (me.is_admin || me.is_coordinator) ? 'true' : 'false';
  document.body.dataset.isYouthBoard = me.is_youth_board ? 'true' : 'false';
  render(appHeaderTemplate(me, LATEST_PATH), HEADER_EL);
  wireAppHeader(HEADER_EL, { onSignout });
  // After lit re-paints the header the bell subtree is fresh DOM; refreshBell
  // restores the badge count from STATE without a redundant API call.
  refreshBell();
}

async function onSignout() {
  try { await api.post('/api/auth/signout'); } catch { /* best-effort */ }
  // Drop any cached Resources file bytes (F012) — login-gated; the next user on
  // this device must not read them. Best-effort; the cache is also gone on reload
  // of a now-unauthenticated app.
  try { navigator.serviceWorker?.controller?.postMessage({ type: 'purge-resources' }); } catch { /* no SW */ }
  $session.set(null);
  // Full reload clears in-memory state; the boot path lands on the auth flow.
  location.assign('/');
}
